import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CommandResultSnackbar, type CommandResult } from '@/components/CommandResultSnackbar';
import { DENSITY_PRESETS, JsonFormsThemeProvider } from '@/components/forms/JsonFormsTheme';
import { formFieldCellSx, formFieldGridSx } from '@/components/forms/formFieldGrid';
import { ValidationErrorBanner } from '@/components/forms/ValidationErrorBanner';
import { ValidationErrorPanel } from '@/components/forms/ValidationErrorPanel';
import { SaveMenuButton } from '@/components/SaveMenuButton';
import { ViewToolbar } from '@/components/ViewToolbar';
import { cancelShortcutLabel } from '@/hooks/useSaveShortcut';
import { ValidationError, toFormErrors } from '@/lib/validationErrors';
import { aiModelLabel } from '@/pages/core/aiModelLabel';
import { AiRoleModelsSection } from '@/pages/core/AiRoleModelsSection';
import { AiUsageSection } from '@/pages/core/AiUsageSection';
import { sectionHeadingSx } from '@/pages/core/sectionHeadingSx';
import { useRoleModelsDraft, type RoleModelsDraft } from '@/pages/core/useRoleModelsDraft';
import {
  aiAssistantSettingsKeys,
  useAiAssistantSettings,
  useAiModels,
  useTestAiConnection,
  useUpdateAiAssistantSettings,
} from '@/hooks/useAiAssistantSettings';
import {
  AI_PROVIDERS,
  type AiAssistantSettings,
  type AiConnectionTestResult,
  type AiProvider,
} from '@/services/aiAssistantSettingsService';

/** The prefix every Anthropic key starts with; the server refuses anything else. */
const ANTHROPIC_KEY_PREFIX = 'sk-ant-';

/** The toolbar's secondary action: outlined, greyed while it cannot run, like the detail views' Cancel. */
const toolbarButtonSx = (inactive: boolean) => ({
  textTransform: 'none',
  fontWeight: 500,
  px: 2,
  ...(inactive ? { borderColor: 'grey.300', color: 'grey.400' } : { borderColor: 'grey.400', color: 'text.secondary' }),
});

/** Each setting a sixth of the panel, the generated forms' default field width. */
const fieldCellSx = formFieldCellSx(2);

/** A server from before per-provider settings sends no `providers`; the one in force is still known. */
function providerState(settings: AiAssistantSettings, provider: AiProvider) {
  return settings.providers?.find((p) => p.provider === provider)
    ?? (provider === settings.provider
      ? { provider, model: settings.model, apiKeySet: settings.apiKeySet }
      : { provider, model: '', apiKeySet: false });
}

interface Draft {
  settings: AiAssistantSettings;
  provider: AiProvider;
  model: string;
  apiKey: string;
  clearKey: boolean;
  roles: RoleModelsDraft;
}

function isNotAnAnthropicKey(provider: AiProvider, key: string): boolean {
  return provider === 'Anthropic' && key !== '' && !key.startsWith(ANTHROPIC_KEY_PREFIX);
}

const keyEdited = (key: string, clearKey: boolean) => key !== '' || clearKey;

/** What the edited values allow: whether anything changed, whether it can be saved, whether the saved state can be tested. */
function draftState({ settings, provider, model, apiKey, clearKey, roles }: Draft, saving: boolean, testing: boolean) {
  const current = providerState(settings, provider);
  const trimmedKey = apiKey.trim();
  const keyLooksWrong = isNotAnAnthropicKey(provider, trimmedKey);
  const providerEdited = provider !== settings.provider || model.trim() !== current.model || keyEdited(trimmedKey, clearKey);
  const hasUnsavedChanges = providerEdited || roles.dirty;
  const savable = model.trim() !== '' && !keyLooksWrong && roles.savable;
  return {
    current,
    trimmedKey,
    keyLooksWrong,
    hasUnsavedChanges,
    canSave: hasUnsavedChanges && savable && !saving,
    canTest: settings.apiKeySet && !hasUnsavedChanges && !testing,
  };
}

function keyHelpText(keyLooksWrong: boolean, apiKeySet: boolean): string {
  if (keyLooksWrong) return 'aiSettings.apiKeyFormat';
  return apiKeySet ? 'aiSettings.apiKeySet' : 'aiSettings.apiKeyUnset';
}

/**
 * The assistant's provider, model and API key at runtime, for the caller's scope: the host's
 * values are every customer's default, a customer's own override them. Each provider has its own
 * model and key; the stored key is never shown, a new one is sent only when typed, and the
 * connection test runs against what is saved. Laid out like a detail view: the toolbar carries
 * Cancel, Save and the test, the settings sit in one section, a customer's model per role in the
 * next, and what the questions cost per user in the last.
 */
export function AiAssistantSettingsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: settings, isLoading } = useAiAssistantSettings();
  const update = useUpdateAiAssistantSettings();
  const test = useTestAiConnection();
  const [provider, setProvider] = useState<AiProvider>('Anthropic');
  const [model, setModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [clearKey, setClearKey] = useState(false);
  const [outcome, setOutcome] = useState<CommandResult | null>(null);
  const [apiValidationErrors, setApiValidationErrors] = useState<ValidationError[]>([]);
  const [validationPanelOpen, setValidationPanelOpen] = useState(false);
  const { data: catalog } = useAiModels(provider);
  const roles = useRoleModelsDraft(settings);

  useEffect(() => {
    if (settings) {
      setProvider(settings.provider);
      setModel(settings.model);
    }
  }, [settings]);

  if (isLoading || !settings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const providerLabel = t(`aiSettings.provider${provider}`);
  const offered = (catalog ?? []).filter((m) => m.toolCalling);
  const { current, trimmedKey, keyLooksWrong, hasUnsavedChanges, canSave, canTest } = draftState(
    { settings, provider, model, apiKey, clearKey, roles },
    update.isPending,
    test.isPending,
  );
  const onError = (error: unknown) => setApiValidationErrors(toFormErrors(error, t('common.errorOccurred')));

  const switchProvider = (next: AiProvider) => {
    setProvider(next);
    setModel(providerState(settings, next).model);
    setApiKey('');
    setClearKey(false);
  };

  const cancel = () => {
    setProvider(settings.provider);
    setModel(settings.model);
    setApiKey('');
    setClearKey(false);
    roles.reset();
    setApiValidationErrors([]);
  };

  const refresh = () => void queryClient.invalidateQueries({ queryKey: aiAssistantSettingsKeys.all });

  const save = () => {
    if (!canSave) return;
    setApiValidationErrors([]);
    update.mutate(
      { provider, model: model.trim(), apiKey: trimmedKey || undefined, clearApiKey: clearKey, ...roles.changes() },
      {
        onSuccess: () => {
          setApiKey('');
          setClearKey(false);
          setOutcome({ severity: 'success', message: t('aiSettings.saved') });
        },
        onError,
      },
    );
  };

  const describeTest = (result: AiConnectionTestResult): CommandResult => result.succeeded
    ? {
        severity: 'success',
        message: t('aiSettings.testSucceeded', {
          provider: t(`aiSettings.provider${result.provider}`),
          model: result.model,
          inputTokens: result.inputTokens,
          outputTokens: result.outputTokens,
        }),
      }
    : { severity: 'error', message: t('aiSettings.testFailed', { message: result.message ?? '' }) };

  const runTest = () => {
    setApiValidationErrors([]);
    test.mutate(undefined, { onSuccess: (result) => setOutcome(describeTest(result)), onError });
  };

  const keyHelp = t(keyHelpText(keyLooksWrong, current.apiKeySet));

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <ValidationErrorBanner
        errors={apiValidationErrors}
        onExpand={() => setValidationPanelOpen(true)}
        onClear={() => {
          setApiValidationErrors([]);
          setValidationPanelOpen(false);
        }}
      />

      <ViewToolbar
        onRefresh={refresh}
        hasUnsavedChanges={hasUnsavedChanges}
        onSave={save}
        saveDisabled={!canSave}
        onCancel={cancel}
        cancelDisabled={!hasUnsavedChanges || update.isPending}
      >
        <Tooltip title={cancelShortcutLabel()}>
          <span>
            <Button
              variant="outlined"
              onClick={cancel}
              disabled={!hasUnsavedChanges || update.isPending}
              sx={toolbarButtonSx(!hasUnsavedChanges || update.isPending)}
            >
              {t('common.cancel')}
            </Button>
          </span>
        </Tooltip>
        <SaveMenuButton
          entity="AiAssistantSettings"
          onSave={save}
          isSaving={update.isPending}
          saveDisabled={!canSave}
        />
        <Tooltip title={t('aiSettings.testHelp')}>
          <span>
            <Button variant="outlined" onClick={runTest} disabled={!canTest} sx={toolbarButtonSx(!canTest)}>
              {t('aiSettings.testConnection')}
            </Button>
          </span>
        </Tooltip>
      </ViewToolbar>

      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', px: 2, pb: 2, bgcolor: 'background.default' }}>
          <Typography variant="h5" sx={{ mt: 1.5, mb: 1, fontWeight: 500 }}>{t('aiSettings.title')}</Typography>

          <Paper sx={{ p: 1.5, mb: 1.5 }} elevation={1}>
            <Typography variant="subtitle2" gutterBottom fontWeight={600} sx={{ mb: 0.5, ...sectionHeadingSx }}>
              {t('aiSettings.sectionSettings')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {settings.scope === 'Host' ? t('aiSettings.scopeHost') : t('aiSettings.scopeTenant')}
            </Typography>
            <JsonFormsThemeProvider density="compact" variant="standard">
              <Box sx={formFieldGridSx(DENSITY_PRESETS.compact.gridGap)}>
                <Box sx={fieldCellSx}>
                  <TextField
                    select
                    id="ai-settings-provider"
                    label={t('aiSettings.provider')}
                    value={provider}
                    onChange={(e) => switchProvider(e.target.value as AiProvider)}
                    helperText={t('aiSettings.providerHelp')}
                    fullWidth
                  >
                    {AI_PROVIDERS.map((p) => (
                      <MenuItem key={p} value={p}>{t(`aiSettings.provider${p}`)}</MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box sx={fieldCellSx}>
                  {offered.length > 0 ? (
                    <TextField
                      select
                      id="ai-settings-model"
                      label={t('aiSettings.model')}
                      value={offered.some((m) => m.id === model) ? model : ''}
                      onChange={(e) => setModel(e.target.value)}
                      helperText={t('aiSettings.modelFromCatalog')}
                      fullWidth
                    >
                      {offered.map((option) => (
                        <MenuItem key={option.id} value={option.id}>{aiModelLabel(t, option)}</MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField
                      label={t('aiSettings.model')}
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                      helperText={t('aiSettings.modelHelp')}
                      fullWidth
                      slotProps={{ htmlInput: { 'data-testid': 'ai-settings-model' } }}
                    />
                  )}
                </Box>
                <Box sx={fieldCellSx}>
                  <TextField
                    label={t('aiSettings.apiKeyFor', { provider: providerLabel })}
                    type="password"
                    value={apiKey}
                    onChange={(e) => {
                      setApiKey(e.target.value);
                      setClearKey(false);
                    }}
                    placeholder={current.apiKeySet ? '••••••••••••' : ''}
                    error={keyLooksWrong}
                    helperText={keyHelp}
                    autoComplete="off"
                    fullWidth
                    slotProps={{ htmlInput: { 'data-testid': 'ai-settings-api-key' } }}
                  />
                  {current.apiKeySet && (
                    <FormControlLabel
                      sx={{ mt: 0.5 }}
                      control={(
                        <Checkbox
                          size="small"
                          checked={clearKey}
                          onChange={(e) => {
                            setClearKey(e.target.checked);
                            if (e.target.checked) setApiKey('');
                          }}
                        />
                      )}
                      label={t('aiSettings.clearApiKey')}
                    />
                  )}
                </Box>
              </Box>
            </JsonFormsThemeProvider>
          </Paper>

          {settings.scope === 'Tenant' && (
            <AiRoleModelsSection draft={roles} settings={settings} />
          )}

          <AiUsageSection />
        </Box>

        <ValidationErrorPanel
          errors={apiValidationErrors}
          open={validationPanelOpen}
          onClose={() => setValidationPanelOpen(false)}
        />
      </Box>

      <CommandResultSnackbar result={outcome} onClose={() => setOutcome(null)} />
    </Box>
  );
}
